/**
 * Guards against open redirects when navigating to a path that did not come from our own source
 * code — notification `actionUrl`s, dashboard `actionPath`s, and anything else supplied by an API
 * response rather than written as a literal in a component.
 *
 * Why this exists at the application level rather than being left to the router:
 *
 * The installed react-router (6.x) is affected by GHSA-wrjc-x8rr-h8h6 — an open redirect through
 * backslashes in `<Link>`/`useNavigate` that bypasses the earlier CVE-2025-68470 fix. The advisory's
 * fixed range starts above 7.17.0, so "just patch it" means a react-router v6 to v7 migration across
 * 100+ routes. That is a planned piece of work, not a security hotfix.
 *
 * This closes the actual vulnerability now, independently of the library version: a value that is
 * not an unambiguously internal path never reaches the router at all. It stays useful after the
 * upgrade too — not trusting server-supplied navigation targets is correct regardless.
 *
 * Rejected forms and why:
 *   `//evil.com`      protocol-relative — the browser treats it as an absolute URL
 *   `/\evil.com`      backslash variant — the specific react-router bypass
 *   `\\evil.com`      leading backslashes, normalised to `//` by browsers
 *   `https://evil.com`, `javascript:`, `data:`  absolute/scheme URLs
 *   anything not starting with `/`  relative paths can escape the intended location
 */

/**
 * Control characters (U+0000–U+001F, U+007F) are stripped or normalised by browsers and can be used
 * to hide a scheme, e.g. a newline inserted before `javascript:`. Checked by code point rather than
 * a regex literal so no raw control byte ever has to appear in this source file.
 */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/** True when `path` is a safe, unambiguously in-app destination. */
export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== 'string') return false;

  const trimmed = path.trim();
  if (trimmed === '') return false;

  if (hasControlChars(trimmed)) return false;

  // Browsers normalise backslashes to forward slashes in URLs, so treat them as equivalent
  // before testing — `/\evil.com` must be judged as `//evil.com`, which it becomes.
  const normalised = trimmed.replace(/\\/g, '/');

  // Must be an absolute in-app path...
  if (!normalised.startsWith('/')) return false;
  // ...but not protocol-relative, which points off-site.
  if (normalised.startsWith('//')) return false;

  return true;
}

/**
 * Returns `path` when safe, otherwise `fallback` (default `'/'`).
 * Use at every navigation whose target came from an API response.
 */
export function safeInternalPath(path: unknown, fallback = '/'): string {
  return isSafeInternalPath(path) ? path.trim() : fallback;
}
